"use client"

import { ProductCategory } from "@medusajs/medusa"
import Link from "next/link"
import { useEffect, useState, useRef } from "react"

import { buildRoute } from "@lib/routes"
import { LeftChevron } from "@modules/navigation/icons/LeftChevron"
import { RightChevron } from "@modules/navigation/icons/RightChevron"

interface CategoriesButtonProps {
  categories: ProductCategory[]
}

export const WebCategoriesButton = ({ categories }: CategoriesButtonProps) => {
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null)
  const [showCategories, setShowCategories] = useState<boolean>(false)
  const categoriesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedCategory(null)
  }, [showCategories])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        setShowCategories(false)
      }
    }

    if (showCategories) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }
  }, [showCategories])

  return (
    <div className="hidden lg:block">
      <div className="group relative" ref={categoriesRef}>
        <button
          onClick={() => setShowCategories(true)}
          className="flex items-center justify-center px-3 py-[6px] font-manrope text-[14px] font-medium leading-[28px] hover:rounded-full hover:bg-nelo-light-gray"
        >
          Categorías
        </button>
        {showCategories && (
          <div className="triangle-on-top absolute -left-4 top-full z-50 flex max-h-[400px] w-[313px] overflow-y-auto rounded-2xl bg-white py-[16px] text-base drop-shadow-2xl">
            {selectedCategory ? (
              <div className="w-full">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="flex w-full items-center gap-4 px-[24px] py-[13px] text-left leading-[20px] hover:bg-nelo-mid-gray"
                >
                  <LeftChevron /> Regresar
                </button>
                <Link
                  className="block w-full px-[24px] py-[14px] leading-[20px] tracking-[-2%] hover:bg-nelo-mid-gray"
                  href={buildRoute.category(selectedCategory.handle)}
                >
                  {selectedCategory.name}
                </Link>
                <div className="divide-y border-t">
                  {selectedCategory.category_children.map((category) => (
                    <Link
                      className="block w-full px-[24px] py-[14px] leading-[20px] tracking-[-2%] hover:bg-nelo-mid-gray"
                      key={category.id}
                      href={buildRoute.category([selectedCategory.handle, category.handle])}
                    >
                      {category.name}
                    </Link>
                  ))}
                  <Link
                    className="block w-full px-[24px] py-[14px] leading-[20px] tracking-[-2%] hover:bg-nelo-mid-gray"
                    href={buildRoute.category(selectedCategory.handle)}
                  >
                    Ver todo en {selectedCategory.name}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="w-full divide-y">
                <p className="block w-full px-[24px] py-[13px] font-semibold leading-[20px]">Ver todas las categorías</p>
                {categories
                  .sort((a, b) => b.category_children.length - a.category_children.length)
                  .map((category) =>
                    category.category_children.length > 0 ? (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category)}
                        className="flex w-full items-center justify-between px-[24px] py-[13px] text-left leading-[20px] hover:bg-nelo-mid-gray"
                      >
                        {category.name}
                        <RightChevron />
                      </button>
                    ) : (
                      <Link
                        key={category.id}
                        href={buildRoute.category(category.handle)}
                        className="block w-full px-[24px] py-[14px] leading-[20px] tracking-[-2%] hover:bg-nelo-mid-gray"
                      >
                        {category.name}
                      </Link>
                    )
                  )}
              </div>
            )}
          </div>
        )}
      </div>
      {showCategories && (
        <div onClick={() => setShowCategories(false)} className="absolute left-0 top-[80px] z-40 h-[calc(100vh-80px)] w-full bg-black/30" />
      )}
    </div>
  )
}
